import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useInfiniteBettingHistory } from '../../queries';
import { generateDateRange } from '../../utils/date';

export type NeobetMyBetsStatus = 'all' | 'open' | 'won' | 'lost' | 'cashout' | 'cancelled' | 'refunded';
export type NeobetMyBetsPreset = 'recent' | 'today' | 'week' | 'month';

type NeobetMyBetsCustomDateRange = {
  fromTimestamp: number;
  toTimestamp: number;
};

export type NeobetMyBetsControlOption<T extends string> = {
  value: T;
  labelKey: string;
  fallback: string;
};

export const NEOBET_MY_BETS_STATUS_OPTIONS: readonly NeobetMyBetsControlOption<NeobetMyBetsStatus>[] = [
  { value: 'all', labelKey: 'all', fallback: 'All' },
  { value: 'open', labelKey: 'open', fallback: 'Open' },
  { value: 'won', labelKey: 'won', fallback: 'Won' },
  { value: 'lost', labelKey: 'lost', fallback: 'Lost' },
  { value: 'cashout', labelKey: 'cashedOut', fallback: 'Cashed Out' },
  { value: 'cancelled', labelKey: 'cancelled', fallback: 'Cancelled' },
  { value: 'refunded', labelKey: 'refunded', fallback: 'Refunded' },
] as const;

export const NEOBET_MY_BETS_PRESET_OPTIONS: readonly NeobetMyBetsControlOption<NeobetMyBetsPreset>[] = [
  { value: 'recent', labelKey: 'recentBets', fallback: 'Recent Bets' },
  { value: 'today', labelKey: 'today', fallback: 'Today' },
  { value: 'week', labelKey: 'forWeek', fallback: 'For Week' },
  { value: 'month', labelKey: 'forMonth', fallback: 'For Month' },
] as const;

export const NEOBET_MY_BETS_RECENT_HOURS = 6;

const PAGE_SIZE = 10;

const STATUS_QUERY_MAP: Record<Exclude<NeobetMyBetsStatus, 'all'>, string[]> = {
  open: ['open'],
  won: ['won'],
  lost: ['lost'],
  cashout: ['cashout'],
  cancelled: ['cancelled'],
  refunded: ['void'],
};

export const getNeobetMyBetsResolveStatuses = (status: NeobetMyBetsStatus) => {
  if (status === 'all') {
    return undefined;
  }

  return STATUS_QUERY_MAP[status];
};

export const getNeobetMyBetsDateRange = (preset: NeobetMyBetsPreset) => {
  if (preset === 'recent') {
    const now = dayjs();

    return {
      fromTimestamp: now.subtract(NEOBET_MY_BETS_RECENT_HOURS, 'hour').valueOf(),
      toTimestamp: now.valueOf(),
    };
  }

  const dateRangeOption = preset === 'today' ? 'today' : preset === 'week' ? 'last_7_days' : 'last_30_days';
  const { fromDate, toDate } = generateDateRange(dateRangeOption);

  return {
    fromTimestamp: fromDate.valueOf(),
    toTimestamp: toDate.valueOf(),
  };
};

export const useNeobetMyBetsPage = () => {
  const [activeStatus, setActiveStatus] = useState<NeobetMyBetsStatus>('all');
  const [activePreset, setActivePreset] = useState<NeobetMyBetsPreset | null>('recent');
  const [customDateRange, setCustomDateRange] = useState<NeobetMyBetsCustomDateRange | null>(null);

  const dateRange = useMemo(
    () => customDateRange ?? getNeobetMyBetsDateRange(activePreset ?? 'recent'),
    [activePreset, customDateRange]
  );
  const resolveStatuses = useMemo(() => getNeobetMyBetsResolveStatuses(activeStatus), [activeStatus]);

  const queryParams = useMemo(
    () => ({
      limit: PAGE_SIZE,
      includeCashoutAmount: true,
      ...(resolveStatuses ? { resolveStatuses } : {}),
      ...(dateRange.fromTimestamp ? { fromTimestamp: dateRange.fromTimestamp } : {}),
      ...(dateRange.toTimestamp ? { toTimestamp: dateRange.toTimestamp } : {}),
    }),
    [dateRange.fromTimestamp, dateRange.toTimestamp, resolveStatuses]
  );

  const { items, isLoading, isFetching, isFetchingNextPage, hasNextPage, lastElementRef } = useInfiniteBettingHistory(
    queryParams,
    {
      refetchOnWindowFocus: false,
    }
  );

  const handleStatusChange = useCallback((status: NeobetMyBetsStatus) => {
    setActiveStatus(status);
  }, []);

  const handlePresetChange = useCallback((preset: NeobetMyBetsPreset) => {
    setActivePreset(preset);
    setCustomDateRange(null);
  }, []);

  const handleCustomDateRangeChange = useCallback((dateRangeValue: NeobetMyBetsCustomDateRange) => {
    setActivePreset(null);
    setCustomDateRange(dateRangeValue);
  }, []);

  return {
    statusOptions: NEOBET_MY_BETS_STATUS_OPTIONS,
    presetOptions: NEOBET_MY_BETS_PRESET_OPTIONS,
    activeStatus,
    activePreset,
    items,
    queryParams,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    lastElementRef,
    handleStatusChange,
    handlePresetChange,
    handleCustomDateRangeChange,
  };
};
